/*
 * TODO: Configuration Extraction Plan
 *
 * The following should be extracted to external files for better maintainability:
 * 1. getDefaultConfig() - Default values for all configuration options
 * 2. configOptions[] - Configuration option definitions (labels, types, categories, etc.)
 * 3. reinitializationOptions - Set of options that require component reinitialization
 *
 * This will allow:
 * - Automatic synchronization with actual component defaults
 * - Easier maintenance when new options are added
 * - Single source of truth for configuration metadata
 */

import { useState, useEffect, useRef } from "react";
import { DEFAULT_ATTRIBUTES } from "../external/component-consts";
import PreoWidget from "./PreoWidget";
import PreoWidgetSimple from "./PreoWidgetSimple";
import { Button } from "zudoku/ui/Button";
import { Card } from "zudoku/ui/Card";
import { Label } from "zudoku/ui/Label";

interface ConfigOption {
  key: string;
  label: string;
  type: "text" | "boolean" | "select" | "color";
  options?: { value: string; label: string }[];
  description?: string;
  category: string;
  requiresReinitialization?: boolean;
}

// TODO: Extract to external configuration file
const getDefaultConfig = () => ({
  "sdk-id":
    "dev:mk:org_38wOTmXJjmzyevTW1MeDRqYqPHa:edt_38wOaCta5xFOFu8h6VQNBsZadao",
  "script-url":
    "https://edge.preo.cloud/c/components@0.6/components.umd.js",
  ...Object.entries(DEFAULT_ATTRIBUTES).reduce((acc, [key, value]) => {
    // Convert from camelCase to kebab-case
    const kebabKey = key.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
    return { ...acc, [kebabKey]: value };
  }, {}),
});

export default function PreoConfigEditor() {
  const widgetRef = useRef<{ reinitialize: () => void }>(null);
  const [forceReinitKey, setForceReinitKey] = useState(0);

  // Default configuration - TODO: Replace with extracted defaults
  const defaultConfig = getDefaultConfig();

  const [config, setConfig] = useState<Record<string, any>>(defaultConfig);

  // Helper function to get URL params
  const getUrlParams = () => {
    if (typeof window === "undefined") return new URLSearchParams();
    return new URLSearchParams(window.location.search);
  };

  // Helper function to update URL params
  const updateUrlParams = (newConfig: Record<string, any>) => {
    if (typeof window === "undefined") return;

    const url = new URL(window.location.href);

    // Clear existing params
    url.search = "";

    // Add all config values to URL for complete shareability
    Object.entries(newConfig).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        // For booleans, convert to string
        if (typeof value === "boolean") {
          url.searchParams.set(key, value ? "true" : "false");
        } else {
          url.searchParams.set(key, String(value));
        }
      }
    });

    // Update URL without page reload
    window.history.replaceState({}, "", url.toString());
  };

  // Load config from URL on mount
  useEffect(() => {
    const urlParams = getUrlParams();
    const urlConfig = { ...defaultConfig };

    // Parse URL params and apply to config
    for (const [key, value] of urlParams.entries()) {
      // Convert string values back to appropriate types
      if (value === "true") {
        urlConfig[key] = true;
      } else if (value === "false") {
        urlConfig[key] = false;
      } else {
        urlConfig[key] = value;
      }
    }

    setConfig(urlConfig);

    // If URL has no params, update URL with default config for shareability
    if (urlParams.toString() === "") {
      updateUrlParams(urlConfig);
    }
  }, []);

  // TODO: Extract to external configuration file
  // Options that require full component reinitialization
  const reinitializationOptions = new Set([
    "product-image-mode",
    "sdk-id",
    "locale",
    "main-categories",
    "cross-sell-categories",
    "is-catalogue",
  ]);

  // TODO: Extract to external configuration file
  const configOptions: ConfigOption[] = [
    {
      key: "sdk-id",
      label: "SDK ID",
      type: "text",
      category: "General",
      requiresReinitialization: true,
    },
    {
      key: "locale",
      label: "Locale",
      type: "text",
      category: "General",
      requiresReinitialization: true,
    },
    {
      key: "tracking-consent",
      label: "Tracking Consent",
      type: "boolean",
      category: "General",
    },
    {
      key: "analytics-integrations",
      label: "Analytics Integrations",
      type: "text",
      category: "General",
    },
    {
      key: "debug",
      label: "Debug Mode",
      type: "boolean",
      category: "Developer",
    },

    // Product selection
    {
      key: "main-categories",
      label: "Product Categories",
      type: "text",
      category: "Products",
      requiresReinitialization: true,
    },
    {
      key: "cross-sell-categories",
      label: "Multi-step Categories",
      type: "text",
      category: "Products",
      requiresReinitialization: true,
    },

    // UI options
    {
      key: "product-image-mode",
      label: "Product Image Mode",
      type: "select",
      options: [
        { value: "zoom", label: "Zoom" },
        { value: "static", label: "Static" },
        { value: "full", label: "Full" },
      ],
      category: "UI",
      requiresReinitialization: true,
    },
    {
      key: "sticky-summary",
      label: "Sticky Summary",
      type: "boolean",
      category: "UI",
    },
    {
      key: "hide-footer",
      label: "Hide Footer",
      type: "boolean",
      category: "UI",
    },
    {
      key: "is-catalogue",
      label: "Catalogue Mode",
      type: "boolean",
      category: "UI",
      requiresReinitialization: true,
    },

    // Colors
    {
      key: "color-brand",
      label: "Brand Color",
      type: "color",
      category: "Colors",
    },
    {
      key: "color-complementary",
      label: "Complementary Color",
      type: "color",
      category: "Colors",
    },
    {
      key: "color-text-primary",
      label: "Text Primary",
      type: "color",
      category: "Colors",
    },
    {
      key: "color-text-secondary",
      label: "Text Secondary",
      type: "color",
      category: "Colors",
    },

    // Cart options
    {
      key: "hide-cart-summary",
      label: "Hide Cart Summary",
      type: "boolean",
      category: "Cart",
    },
    {
      key: "disable-local-storage-cart",
      label: "Disable Cart Persistence",
      type: "boolean",
      category: "Cart",
    },
    {
      key: "show-discount",
      label: "Show Discount Input",
      type: "boolean",
      category: "Cart",
    },
    {
      key: "empty-cart-allowed",
      label: "Allow Empty Cart",
      type: "boolean",
      category: "Cart",
    },

    // User form fields
    {
      key: "has-first-name-field",
      label: "First Name Field",
      type: "boolean",
      category: "Form Fields",
    },
    {
      key: "has-last-name-field",
      label: "Last Name Field",
      type: "boolean",
      category: "Form Fields",
    },
    {
      key: "has-email-field",
      label: "Email Field",
      type: "boolean",
      category: "Form Fields",
    },
    {
      key: "has-confirm-email-field",
      label: "Confirm Email Field",
      type: "boolean",
      category: "Form Fields",
    },
    {
      key: "has-phone-field",
      label: "Phone Field",
      type: "boolean",
      category: "Form Fields",
    },
    {
      key: "has-confirm-phone-field",
      label: "Confirm Phone Field",
      type: "boolean",
      category: "Form Fields",
    },
  ];

  // Group options by category
  const categories = configOptions.reduce(
    (acc, option) => {
      if (!acc[option.category]) {
        acc[option.category] = [];
      }
      acc[option.category].push(option);
      return acc;
    },
    {} as Record<string, ConfigOption[]>,
  );

  const handleChange = (key: string, value: any) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);

    // Update URL with new config
    updateUrlParams(newConfig);

    // Auto-reinitialize for options that require it
    if (reinitializationOptions.has(key)) {
      console.log(`Reinitializing widget due to ${key} change:`, value);
      // Force React remount for critical changes
      setForceReinitKey((prev) => prev + 1);

      // Also call the widget's reinitialize method as backup
      setTimeout(() => {
        widgetRef.current?.reinitialize();
      }, 100);
    }
  };

  const manualReinitialize = () => {
    console.log("Manual reinitialization triggered");
    setForceReinitKey((prev) => prev + 1);
    setTimeout(() => {
      widgetRef.current?.reinitialize();
    }, 100);
  };

  const generateHtmlSnippet = () => {
    const scriptUrl = config['script-url'] || 'https://edge.preo.cloud/c/components@0.6/components.umd.js';
    
    // Filter out script-url from attributes since it goes in the script tag
    const attributes = Object.entries(config)
      .filter(([key]) => key !== "script-url")
      .filter(([, value]) => {
        // Include non-empty strings and true booleans, exclude false booleans
        if (typeof value === "boolean") {
          return value === true;
        }
        return value !== undefined && value !== null && value !== "";
      })
      .map(([key, value]) => {
        // For boolean true values, use bare attribute (no ="true")
        if (typeof value === "boolean" && value === true) {
          return `  ${key}`;
        }
        // For other values, use key="value" format
        return `  ${key}="${value}"`;
      })
      .join("\n");

    return `<!-- Include Preo Components Script -->
<script src="${scriptUrl}" async></script>

<!-- Preo Storefront Widget -->
<preo-storefront
${attributes}
></preo-storefront>`;
  };

  const renderConfigInput = (option: ConfigOption) => {
    const value = config[option.key];

    switch (option.type) {
      case "boolean":
        return (
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => handleChange(option.key, e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
        );
      case "select":
        return (
          <select
            value={value}
            onChange={(e) => handleChange(option.key, e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {option.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      case "color":
        return (
          <input
            type="color"
            value={value || "#000000"}
            onChange={(e) => handleChange(option.key, e.target.value)}
            className="w-16 h-8 rounded border border-gray-300 cursor-pointer"
          />
        );
      default:
        return (
          <input
            type="text"
            value={value || ""}
            onChange={(e) => handleChange(option.key, e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        );
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Preview on top, full width */}
      <Card className="w-full min-h-[600px] p-0 overflow-hidden">
        <PreoWidget key={forceReinitKey} ref={widgetRef} {...config} />
      </Card>

      {/* Configuration options in two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 max-h-[600px] overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4">Configuration Options</h3>
          {Object.entries(categories)
            .slice(0, Math.ceil(Object.keys(categories).length / 2))
            .map(([category, options]) => (
              <div key={category} className="mb-6">
                <h4 className="text-md font-medium mb-3">{category}</h4>
                {options.map((option) => (
                  <div key={option.key} className="mb-4 space-y-2">
                    <Label className="text-sm font-medium block">
                      {option.label}
                      {option.requiresReinitialization && (
                        <span className="text-xs text-gray-500 ml-1">
                          (auto-reinitializes)
                        </span>
                      )}
                    </Label>
                    {renderConfigInput(option)}
                    {option.description && (
                      <p className="text-xs text-gray-600 mt-1">
                        {option.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ))}
        </Card>

        <Card className="p-6 max-h-[600px] overflow-y-auto">
          {Object.entries(categories)
            .slice(Math.ceil(Object.keys(categories).length / 2))
            .map(([category, options]) => (
              <div key={category} className="mb-6">
                <h4 className="text-md font-medium mb-3">{category}</h4>
                {options.map((option) => (
                  <div key={option.key} className="mb-4 space-y-2">
                    <Label className="text-sm font-medium block">
                      {option.label}
                      {option.requiresReinitialization && (
                        <span className="text-xs text-gray-500 ml-1">
                          (auto-reinitializes)
                        </span>
                      )}
                    </Label>
                    {renderConfigInput(option)}
                    {option.description && (
                      <p className="text-xs text-gray-600 mt-1">
                        {option.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ))}

          <div className="flex gap-2 mt-4">
            <Button onClick={manualReinitialize}>Manual Reinitialize</Button>
            <Button
              onClick={() => {
                if (typeof window !== "undefined") {
                  navigator.clipboard.writeText(window.location.href);
                  console.log("URL copied to clipboard");
                }
              }}
              variant="outline"
            >
              Share URL
            </Button>
          </div>
        </Card>
      </div>

      {/* Code snippet at the bottom */}
      <Card className="p-6">
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-md font-medium">HTML Code Snippet</h4>
          <Button
            onClick={() => {
              const htmlCode = generateHtmlSnippet();
              navigator.clipboard.writeText(htmlCode);
              console.log("Code copied to clipboard");
            }}
            variant="outline"
            size="sm"
          >
            Copy Code
          </Button>
        </div>
        <pre className="bg-gray-900 text-gray-100 p-4 rounded-md overflow-auto text-sm font-mono leading-relaxed">
          <code>{generateHtmlSnippet()}</code>
        </pre>
      </Card>
    </div>
  );
}
